import type { ReactNode } from "react";
import { FormattedMessage } from "react-intl";
import { Button } from "../design/Button";
import { Dialog } from "./Dialog";

// Confirm-an-action dialog. Used by every delete/unpin/remove flow.
// Defaults: confirm = destructive (red), cancel = neutral. Pass
// `destructive={false}` for non-destructive confirmations.

interface ConfirmDialogProps {
  open: boolean;
  title: ReactNode;
  message?: ReactNode;
  confirmLabel?: ReactNode;
  cancelLabel?: ReactNode;
  destructive?: boolean;
  confirmTestId?: string;
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
}

export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel,
  cancelLabel,
  destructive = true,
  confirmTestId,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  return (
    <Dialog open={open} onClose={onCancel} title={title}>
      {message ? <div className="mb-6 text-sm text-ink/70">{message}</div> : null}
      <div className="flex items-center justify-end gap-2">
        <Button variant="secondary" onClick={onCancel}>
          {cancelLabel ?? (
            <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
          )}
        </Button>
        <Button
          variant={destructive ? "destructive" : "primary"}
          onClick={() => void onConfirm()}
          data-testid={confirmTestId}
        >
          {confirmLabel ?? (
            <FormattedMessage id="common.confirm" defaultMessage="Confirm" />
          )}
        </Button>
      </div>
    </Dialog>
  );
}
